import React, { useEffect, useState } from "react";
import { Truck, ShieldCheck, PackageX, PackageCheck, Clock, Zap } from "lucide-react";

// 2-letter country code → emoji flag (regional indicator symbols)
function codeToFlag(code) {
  if (!code || code.length !== 2) return "🌍";
  try {
    return String.fromCodePoint(
      ...[...code.toUpperCase()].map((c) => 0x1f1e6 - 65 + c.charCodeAt(0))
    );
  } catch {
    return "🌍";
  }
}

// EU country codes — used to pick the right warehouse origin (USA vs Europe)
const EU_CODES = new Set([
  "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT",
  "LV","LT","LU","MT","NL","PL","PT","RO","SK","SI","ES","SE","GB","CH","NO",
  "IS","LI","UA","RS","BA","ME","MK","AL","XK","BY","MD","RU","TR",
]);

function originForCountry(code) {
  // EU buyers ship from our European warehouse; everyone else from USA
  if (EU_CODES.has((code || "").toUpperCase())) {
    return { label: "European warehouse", flag: "🇪🇺", short: "EU" };
  }
  return { label: "USA warehouse", flag: "🇺🇸", short: "USA" };
}

const LOCAL_KEY = "dp_geo_v1";

// Resolve buyer country via free IP geolocation. Caches result for 24h.
async function resolveCountry() {
  // 1. Try localStorage cache
  try {
    const cached = JSON.parse(localStorage.getItem(LOCAL_KEY) || "null");
    if (cached && cached.ts && Date.now() - cached.ts < 24 * 60 * 60 * 1000) {
      return cached;
    }
  } catch {
    /* no-op */
  }

  // 2. Primary: ipapi.co (HTTPS, free 1k/day, no key required)
  try {
    const r = await fetch("https://ipapi.co/json/", { cache: "no-store" });
    if (r.ok) {
      const j = await r.json();
      const data = {
        code: (j.country_code || "").toUpperCase(),
        name: j.country_name || "",
        ts: Date.now(),
      };
      if (data.code && data.name) {
        localStorage.setItem(LOCAL_KEY, JSON.stringify(data));
        return data;
      }
    }
  } catch {
    /* fall through */
  }

  // 3. Fallback: Cloudflare trace (works in most regions, no JSON parse needed)
  try {
    const r = await fetch("https://www.cloudflare.com/cdn-cgi/trace", { cache: "no-store" });
    const text = await r.text();
    const m = /loc=([A-Z]{2})/.exec(text);
    if (m) {
      const code = m[1];
      // No name from CF — derive via Intl.DisplayNames
      let name = code;
      try {
        const dn = new Intl.DisplayNames(["en"], { type: "region" });
        name = dn.of(code) || code;
      } catch {
        /* keep code */
      }
      const data = { code, name, ts: Date.now() };
      localStorage.setItem(LOCAL_KEY, JSON.stringify(data));
      return data;
    }
  } catch {
    /* no-op */
  }

  // 4. Last resort
  return { code: "", name: "your country", ts: Date.now() };
}

function addBusinessDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function fmtMonthDay(date) {
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function deliveryWindow() {
  const today = new Date();
  const start = addBusinessDays(today, 2);
  const end = addBusinessDays(today, 5);
  // If both fall in the same month: "Jun 22 — 25". If different months: "Jun 22 — Jul 1".
  const sameMonth = start.getMonth() === end.getMonth();
  return sameMonth
    ? `${fmtMonthDay(start)} — ${end.getDate()}`
    : `${fmtMonthDay(start)} — ${fmtMonthDay(end)}`;
}

export default function ShippingTrustBlock() {
  const [geo, setGeo] = useState(null);

  useEffect(() => {
    let alive = true;
    resolveCountry().then((data) => {
      if (alive) setGeo(data);
    });
    return () => {
      alive = false;
    };
  }, []);

  const flag = codeToFlag(geo?.code);
  const countryName = geo?.name || "your country";
  const window = deliveryWindow();
  const origin = originForCountry(geo?.code);

  return (
    <div
      data-testid="shipping-trust-block"
      className="rounded-2xl border border-black/10 bg-white p-3.5 md:p-4 shadow-sm space-y-3"
    >
      {/* Top: shipping confirmed + estimated date with country flag */}
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          data-testid="ship-country-flag"
          className="text-[28px] leading-none mt-0.5 select-none"
        >
          {flag}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[12px] uppercase tracking-[0.16em] font-extrabold text-[#22c55e] inline-flex items-center gap-1.5">
            <Truck size={13} /> Free shipping to {countryName}
          </div>
          <div
            data-testid="ship-eta"
            className="mt-1 text-[14px] md:text-[15px] font-bold text-[#0e0e10] tabular-nums"
          >
            Estimated delivery: <span className="text-[#0e0e10]">{window}</span>
          </div>
          <div
            data-testid="ship-origin"
            className="text-[11px] text-[#6b6b72] mt-0.5 inline-flex items-center gap-1"
          >
            <span aria-hidden>{origin.flag}</span>
            <span>Ships from our {origin.label} within 24h</span>
          </div>
        </div>
      </div>

      {/* US Company + Security badges row */}
      <div
        data-testid="ship-us-company-row"
        className="border-t border-dashed border-black/10 pt-2.5 grid grid-cols-2 gap-2 text-[11px] font-bold text-[#0e0e10]"
      >
        <div className="inline-flex items-center gap-1.5">
          <span aria-hidden className="text-[14px] leading-none">🇺🇸</span>
          <span>Registered US Company</span>
        </div>
        <div className="inline-flex items-center gap-1.5">
          <ShieldCheck size={13} className="text-[#22c55e]" />
          <span>Total Security · SSL 256-bit</span>
        </div>
      </div>

      {/* Trust badges grid */}
      <div className="border-t border-dashed border-black/10 pt-3 grid grid-cols-2 gap-x-3 gap-y-2.5 text-[11.5px] text-[#3a3a40] font-semibold">
        <div className="flex items-start gap-1.5">
          <Zap size={13} className="text-[#0e0e10] mt-0.5 shrink-0" />
          <span>
            <strong className="block text-[#0e0e10]">Processed in 24h</strong>
            <span className="text-[10.5px] text-[#6b6b72] font-medium">
              From USA & EU warehouses
            </span>
          </span>
        </div>
        <div className="flex items-start gap-1.5">
          <Clock size={13} className="text-[#0e0e10] mt-0.5 shrink-0" />
          <span>
            <strong className="block text-[#0e0e10]">Fast delivery</strong>
            <span className="text-[10.5px] text-[#6b6b72] font-medium">
              Tracked · 2-5 business days
            </span>
          </span>
        </div>
        <div className="flex items-start gap-1.5">
          <PackageX size={13} className="text-[#0e0e10] mt-0.5 shrink-0" />
          <span>
            <strong className="block text-[#0e0e10]">Refund if lost</strong>
            <span className="text-[10.5px] text-[#6b6b72] font-medium">
              Full refund on lost parcels
            </span>
          </span>
        </div>
        <div className="flex items-start gap-1.5">
          <PackageCheck size={13} className="text-[#0e0e10] mt-0.5 shrink-0" />
          <span>
            <strong className="block text-[#0e0e10]">20-day promise</strong>
            <span className="text-[10.5px] text-[#6b6b72] font-medium">
              Doesn&apos;t arrive in 20 days? Full refund.
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}
